import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft, GitBranch } from "lucide-react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";

const ERA_COLOR = {
  foundations: "bg-foreground text-background",
  ml: "bg-brand-cyber text-white",
  deeplearning: "bg-brand-pink text-white",
  llm: "bg-brand-tangerine text-brand-ink",
  genai: "bg-brand-lime text-brand-ink",
  agentic: "bg-brand-cyber text-white",
  future: "bg-brand-lime text-brand-ink",
};

function Milestone({ m, index }) {
  const future = !!m.future;
  return (
    <motion.div
      initial={{ opacity: 0, x: 16 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.04, 0.25) }}
      className="relative pl-[76px] sm:pl-[92px] pb-6"
      data-testid="timeline-milestone"
    >
      {/* year marker on the spine */}
      <span className={`absolute left-0 top-0 grid place-items-center h-10 w-14 sm:w-16 border border-border font-mono text-[11px] font-extrabold shadow-brutal-sm ${ERA_COLOR[m.era] || "bg-card"}`}>
        {m.year}
      </span>
      <div className={`bg-card border border-border shadow-brutal-sm p-4 ${future ? "border-dashed" : ""}`}>
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-heading font-extrabold tracking-tight text-base md:text-lg leading-tight">{m.title}</h3>
          {future && <span className="font-mono text-[9px] font-bold uppercase bg-brand-lime text-brand-ink border border-border px-1.5 py-0.5">Future</span>}
        </div>
        <p className="mt-1 text-sm font-medium text-foreground/75 leading-relaxed">{m.blurb}</p>
      </div>
    </motion.div>
  );
}

export default function Timeline() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);

  const load = async () => {
    setLoading(true); setErr(false);
    try {
      const { data } = await api.get("/universe/timeline");
      setData(data);
    } catch {
      setErr(true);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background grid-paper">
        <Navbar />
        <div className="py-40 grid place-items-center" data-testid="timeline-loading">
          <div className="grid place-items-center h-16 w-16 bg-brand-pink text-white border border-border shadow-brutal">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        </div>
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen bg-background grid-paper">
        <Navbar />
        <div className="max-w-md mx-auto px-4 pt-40 text-center" data-testid="timeline-error">
          <p className="font-heading font-extrabold text-xl">Couldn't load the timeline</p>
          <p className="mt-2 text-sm text-muted-foreground">Please check your connection and try again.</p>
          <button onClick={load} className="mt-5 bg-brand-pink text-white border border-border shadow-brutal-sm brutal-press px-4 py-2 font-heading font-bold text-sm">Retry</button>
        </div>
      </div>
    );
  }

  const eras = data.eras || [];
  const milestones = data.milestones || [];

  return (
    <div className="min-h-screen bg-background grid-paper">
      <Navbar />
      <PageTransition className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24">
        <button data-testid="timeline-back" onClick={() => navigate("/universe")} className="inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-wider text-muted-foreground hover:text-foreground mb-5">
          <ArrowLeft className="h-4 w-4" /> AI Universe
        </button>

        <div className="bg-brand-pink text-white border border-border shadow-brutal p-6 md:p-8" data-testid="timeline-header">
          <p className="font-mono text-xs font-bold uppercase tracking-widest text-white/80">1950 → the future</p>
          <h1 className="mt-2 font-heading font-extrabold tracking-tight text-3xl md:text-5xl leading-[1.05]">
            The AI Timeline
          </h1>
          <p className="mt-3 font-medium max-w-2xl text-white/90">
            How AI began, every leap that got us here, and where it's heading next.
          </p>
        </div>

        <div className="mt-10 relative" data-testid="timeline-track">
          {/* the spine */}
          <span className="absolute left-7 sm:left-8 top-2 bottom-0 w-1 bg-foreground" aria-hidden />
          {eras.map((era) => {
            const items = milestones.filter((m) => m.era === era.key);
            if (items.length === 0) return null;
            return (
              <section key={era.key} className="mb-4" data-testid={`timeline-era-${era.key}`}>
                <div className="relative pl-[76px] sm:pl-[92px] mb-4">
                  <div className="bg-foreground text-background border border-border px-3 py-2 inline-block">
                    <span className="font-heading font-extrabold tracking-tight text-sm md:text-base">{era.label}</span>
                    <span className="ml-2 font-mono text-[10px] font-bold uppercase opacity-70">{era.range}</span>
                  </div>
                </div>
                {items.map((m, i) => (
                  <Milestone key={`${m.year}-${m.title}`} m={m} index={i} />
                ))}
              </section>
            );
          })}
        </div>

        <div className="mt-6 bg-secondary border border-dashed border-border p-4 flex items-center gap-3">
          <span className="grid place-items-center h-9 w-9 bg-brand-cyber text-white border border-border"><GitBranch className="h-4 w-4" /></span>
          <p className="text-sm font-medium">
            Want how it all connects? Explore the{" "}
            <button data-testid="timeline-to-mindmap" onClick={() => navigate("/universe/mindmap")} className="font-bold underline underline-offset-2 hover:text-brand-cyber">AI Mind Map</button>.
          </p>
        </div>
      </PageTransition>
    </div>
  );
}
